import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  PlaygroundPreview,
  getPlaygroundPreviewStatus,
  checkPlaygroundPreviewHealth,
  API_BASE_URL,
  getAuthHeaders,
} from '../api';
import './PlaygroundModal.css';

interface PlaygroundModalProps {
  isOpen: boolean;
  onClose: () => void;
  problemId: string;
  software: string;
  problemPrompt?: string;
}

const PlaygroundModal: React.FC<PlaygroundModalProps> = ({
  isOpen,
  onClose,
  problemId,
  software,
  problemPrompt,
}) => {
  const [currentPreview, setCurrentPreview] = useState<PlaygroundPreview | null>(null);
  const [isHealthy, setIsHealthy] = useState(false);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);
  const [healthCheckAttempts, setHealthCheckAttempts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingStartTime, setLoadingStartTime] = useState<Date | null>(null);
  const [loadingElapsedTime, setLoadingElapsedTime] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Always fetch fresh playground preview when modal opens (handles expiration)
      setCurrentPreview(null);
      setHealthCheckAttempts(0);
      setIsHealthy(false);
      setIsCheckingHealth(false);
      setIsLoading(true);
      setError(null);
      setLoadingStartTime(new Date());

      initializePlayground();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, problemId]);

  // Update loading elapsed time
  useEffect(() => {
    if (isLoading && loadingStartTime) {
      const interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - loadingStartTime.getTime()) / 1000);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        setLoadingElapsedTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setLoadingElapsedTime('');
    }
  }, [isLoading, loadingStartTime]);

  const initializePlayground = async () => {
    try {
      // Get initial status - always use "mattermost" as the software for Mattermost previews
      let response = await getPlaygroundPreviewStatus(problemId, 'mattermost');
      setCurrentPreview(response);

      // Poll while creating (max 5 minutes)
      const maxStatusAttempts = 60; // 60 attempts * 5 seconds = 5 minutes
      let statusAttempts = 0;

      while (response.status === 'creating' && statusAttempts < maxStatusAttempts) {
        console.log('[Playground Preview] Status: creating, polling...');
        await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds

        // Poll the status again
        response = await getPlaygroundPreviewStatus(problemId, 'mattermost');
        setCurrentPreview(response);
        statusAttempts++;
      }

      // Check if we got a ready playground
      if (response.status === 'failed') {
        setIsLoading(false);
        setError(response.error || 'Playground creation failed');
        return;
      }

      if (response.status !== 'ready' || !response.id) {
        setIsLoading(false);
        setError(`Playground creation timed out. Status: ${response.status}`);
        return;
      }

      console.log('[Playground Preview] Playground is ready, starting health checks');
      setIsLoading(false);

      // Now start health checks
      setIsCheckingHealth(true);
      setHealthCheckAttempts(0);

      const maxHealthAttempts = 24; // 24 attempts * 5 seconds = 2 minutes
      let healthAttempts = 0;
      let healthy = false;

      while (healthAttempts < maxHealthAttempts && !healthy) {
        try {
          const result = await checkPlaygroundPreviewHealth(response.id);
          healthAttempts++;
          setHealthCheckAttempts(healthAttempts);

          if (result.status === 'healthy') {
            healthy = true;
            setIsHealthy(true);
            console.log('[Playground Preview] Health check passed');
          } else {
            console.log('[Playground Preview] Health check status:', result.status);
            await new Promise(resolve => setTimeout(resolve, 5000));
          }
        } catch (error) {
          console.error('[Playground Preview] Health check failed:', error);
          healthAttempts++;
          setHealthCheckAttempts(healthAttempts);
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
      }

      setIsCheckingHealth(false);

      if (!healthy) {
        console.warn('[Playground Preview] Health checks timed out, but playground may still be accessible');
      }

    } catch (error: any) {
      console.error('Failed to initialize playground:', error);
      setIsLoading(false);
      setError(error?.message || 'Failed to create playground');
    }
  };

  const getIframeSrc = () => {
    // Use modal_sandbox_id (e.g., "sb-uVuqJ5nFZnSIK31i0metwz")
    // and encode it like task-platform does
    let sandboxId = currentPreview?.modal_sandbox_id || currentPreview?.playground_id;
    console.log('[PlaygroundModal] currentPreview:', currentPreview);
    console.log('[PlaygroundModal] sandboxId from field:', sandboxId);

    // Fallback: extract from access_url if modal_sandbox_id is not available (old backend)
    if (!sandboxId && currentPreview?.access_url) {
      console.log('[PlaygroundModal] Extracting sandbox ID from access_url (fallback for old backend)');
      try {
        // Extract the encoded sandbox ID from access_url
        // Format: https://ta-{encoded-sandbox-id}-{port}.wo-{worker-id}.w.modal.host
        const match = currentPreview.access_url.match(/ta-([a-z0-9_]+)-\d+/);
        if (match) {
          // The extracted ID is already encoded, so use it directly
          const url = `https://${match[1]}.playground.habitat.inc`;
          console.log('[PlaygroundModal] Generated iframe URL from access_url:', url);
          return url;
        }
      } catch (error) {
        console.error('[PlaygroundModal] Failed to extract from access_url:', error);
      }
    }

    if (!sandboxId) {
      console.warn('[PlaygroundModal] No sandbox ID found');
      return '';
    }

    // Encode uppercase letters: replace each uppercase letter with _lowercase
    const encodedId = sandboxId.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);
    const url = `https://${encodedId}.playground.habitat.inc`;

    console.log('[PlaygroundModal] Generated iframe URL:', url);
    return url;
  };

  const handleDownloadMattermostMedia = async () => {
    setIsDownloading(true);
    setDownloadError(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/playgrounds/problems/${problemId}/mattermost-media`,
        {
          headers: getAuthHeaders(),
        }
      );

      window.open(response.data.download_url, '_blank');
    } catch (error) {
      console.error('Failed to download Mattermost media files:', error);
      setDownloadError(error instanceof Error ? error.message : 'Failed to download files');
      setTimeout(() => setDownloadError(null), 5000);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen) return null;

  const credentials = {
    username: 'li.na',
    password: 'password',
  };

  return (
    <div className="playground-modal-overlay" onClick={onClose}>
      <div className="playground-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="playground-modal-close" onClick={onClose} aria-label="Close playground modal">
          ✕
        </button>
        <div className="playground-modal-header">
          <div className="header-content">
            <div className="header-main">
              {problemPrompt && (
                <div className="task-section">
                  <div className="task-label">Task</div>
                  <div className="task-description">{problemPrompt}</div>
                </div>
              )}

              <div className="header-sidebar">
                <div className="credentials-section">
                  <div className="credentials-label">Login</div>
                  <div className="credential-row">
                    <span className="credential-key">User:</span>
                    <code className="credential-value">{credentials.username}</code>
                  </div>
                  <div className="credential-row">
                    <span className="credential-key">Pass:</span>
                    <code className="credential-value">{credentials.password}</code>
                  </div>
                </div>

                <button
                  onClick={handleDownloadMattermostMedia}
                  disabled={isDownloading}
                  className="download-button"
                >
                  {isDownloading ? 'Exporting...' : 'Export Mattermost Media Files'}
                </button>

                {downloadError && (
                  <div className="download-error">
                    {downloadError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="playground-modal-body">
          {isLoading && (
            <div className="playground-loading">
              <div className="loading-spinner"></div>
              <h3>Creating Playground...</h3>
              <p>Setting up your Mattermost environment with the problem's initial state.</p>
              {loadingElapsedTime && (
                <p className="loading-timer">Time elapsed: {loadingElapsedTime}</p>
              )}
              <p className="loading-note">This may take up to one minute to load.</p>
              <div className="loading-skeleton">
                <div className="skeleton-bar"></div>
                <div className="skeleton-bar"></div>
                <div className="skeleton-bar short"></div>
              </div>
            </div>
          )}

          {!isLoading && !error && isCheckingHealth && (
            <div className="playground-loading">
              <div className="loading-spinner"></div>
              <h3>Starting Playground...</h3>
              <p>Waiting for Mattermost to become available...</p>
              <div className="health-check-info">
                <small>Health check attempt: {healthCheckAttempts}/24</small>
              </div>
              <div className="loading-skeleton">
                <div className="skeleton-bar"></div>
                <div className="skeleton-bar"></div>
                <div className="skeleton-bar short"></div>
              </div>
            </div>
          )}

          {!isLoading && !error && !isCheckingHealth && !isHealthy && currentPreview?.access_url && (
            <div className="playground-timeout">
              <h3>Playground Timeout</h3>
              <p>The playground took too long to start. You can try refreshing or opening it directly:</p>
              <a
                href={currentPreview.access_url}
                target="_blank"
                rel="noopener noreferrer"
                className="playground-direct-link"
              >
                Open Playground Directly
              </a>
            </div>
          )}

          {!isLoading && !error && (
            <div className="playground-iframe-container">
              <iframe
                src={isHealthy ? getIframeSrc() : 'about:blank'}
                className={`playground-iframe ${!isHealthy ? 'hidden' : ''}`}
                title="Mattermost Playground"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
              />
            </div>
          )}

          {error && (
            <div className="playground-error">
              <h3>Playground Creation Failed</h3>
              <p>There was an error creating the playground:</p>
              <div className="error-details">
                <code>{error}</code>
              </div>
              <p>Please try again or contact support if the issue persists.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaygroundModal;
